import { useState } from 'react'
import { Fade, Grid2, Button, useMediaQuery, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { FormattedMessage } from 'react-intl'
import MoovyVirtualKeyboard from '../moovyVirtualKeyboard'
import LicensePlateTextField from '../../pages/homePage/licensePlateTextField'

interface LicensePlateInputProps {
  useMoovyKeyboard?: boolean
  useButton?: boolean
  onRedeem: () => void
  onValidationError?: (hasError: boolean) => void
  value: string
  onChange: (licensePlate: string) => void
}

type AllowedButton = 'delete' | 'down' | 'OK'
const allowedKeys: AllowedButton[] = ['delete', 'down', 'OK']

export const isValidLicensePlate = (licensePlate?: string): boolean => {
  const pattern = /^[A-ZÅÄÖa-zåäö0-9\\s-]{2,10}$/
  return !!licensePlate && pattern.test(licensePlate)
}

const LicensePlateInput = ({
  useMoovyKeyboard,
  useButton = false,
  onRedeem,
  onValidationError,
  value,
  onChange
}: LicensePlateInputProps) => {
  const theme = useTheme()
  const [open, setOpen] = useState<boolean>(!useButton)
  const [showButton, setShowButton] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [firstValidation, setFirstValidation] = useState<boolean>(false)
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const fadingTimeoutMs = 200

  const handleOpen = () => {
    if (!smallScreen && useMoovyKeyboard) {
      setShowButton(false)
      setTimeout(() => {
        setOpen(true)
      }, fadingTimeoutMs)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setHasError(false)
    if (!smallScreen && useMoovyKeyboard) {
      setTimeout(() => {
        setShowButton(true)
      }, fadingTimeoutMs)
    }
  }

  const redeemPermit = () => {
    const isValid = isValidLicensePlate(value)
    setHasError(!isValid)
    onValidationError && onValidationError(!isValid)

    if (isValid) {
      onRedeem()
      setFirstValidation(false)
    } else {
      handleOpen()
      setFirstValidation(true)
    }
  }

  const handleVirtualKeyboardInput = (key: string) => {
    if (value.length >= 10 && !allowedKeys.includes(key as AllowedButton)) {
      return
    }

    const upperKey = key.toUpperCase()

    if (key === 'delete') {
      const updatedPlate = value.slice(0, -1)
      onChange(updatedPlate)
      if (firstValidation) {
        setHasError(!isValidLicensePlate(updatedPlate))
      }
    } else if (key === 'down') {
      handleClose()
    } else if (key === 'OK') {
      setFirstValidation(true)
      const isValid = isValidLicensePlate(value)
      setHasError(!isValid)
      if (isValid) {
        if (useButton) {
          handleClose()
        } else {
          redeemPermit()
        }
      }
    } else {
      const updatedPlate = value + upperKey
      onChange(updatedPlate)
      if (firstValidation) {
        setHasError(!isValidLicensePlate(updatedPlate))
      }
    }
  }

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.toUpperCase()
    onChange(inputValue)

    if (firstValidation) {
      if (!isValidLicensePlate(inputValue)) {
        setHasError(true)
      } else {
        setHasError(false)
      }
    }
  }

  return (
    <>
      <Box sx={{ width: { xs: 300, sm: 400 } }}>
        <Grid2>
          <LicensePlateTextField
            useMoovyKeyboard={useMoovyKeyboard ?? false}
            value={value}
            onFocus={handleOpen}
            onChange={handleTextChange}
            hasError={hasError}
            redeemPermit={redeemPermit}
          />
        </Grid2>
        {useButton && (
          <Grid2>
            <Fade in={showButton} unmountOnExit>
              <Button
                variant="contained"
                size="large"
                sx={{ height: '60px', marginTop: '10px' }}
                onFocus={redeemPermit}
                fullWidth
              >
                <FormattedMessage id="moovyVirtualKeyboard.button.redeemPermit" />
              </Button>
            </Fade>
          </Grid2>
        )}
      </Box>
      <MoovyVirtualKeyboard
        open={open}
        handleVirtualKeyboardInput={handleVirtualKeyboardInput}
        useMoovyKeyboard={useMoovyKeyboard ?? false}
      />
    </>
  )
}

export default LicensePlateInput
